import React, { useState, useEffect } from 'react';
import { ArrowLeft, ArrowRight, Users, Clock, Loader2, Building2 } from 'lucide-react';
import { caseStudiesData } from '../mock/data';
import { Button } from './ui/button';
import { useTranslation } from 'react-i18next';

import { apiUrl, openBookDemo } from '../utils/config';
import logger from '../utils/logger';

const CaseStudies = ({ onCaseStudyClick, onBackToHome, onAdminClick }) => {
  const { t, i18n } = useTranslation();
  const [caseStudies, setCaseStudies] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchCaseStudies();
  }, [i18n.language]);

  const fetchCaseStudies = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(apiUrl(`case-studies?lang=${i18n.language}`));
      if (response.ok) {
        const data = await response.json();
        // If no data from API, fall back to mock data for demo
        if (data.length === 0) {
          setCaseStudies(caseStudiesData.studies);
        } else {
          setCaseStudies(data);
        }
      } else {
        // Fallback to mock data
        setCaseStudies(caseStudiesData.studies);
      }
    } catch (error) {
      logger.error('Error fetching case studies:', error);
      // Fallback to mock data
      setCaseStudies(caseStudiesData.studies);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <button 
            onClick={onBackToHome}
            className="inline-flex items-center text-gray-600 hover:text-blue-600 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {i18n.language === 'tr' ? 'Ana Sayfaya Dön' : 'Back to Home'}
          </button>
          {onAdminClick && (
            <button
              onClick={onAdminClick}
              className="text-gray-500 hover:text-blue-600 transition-colors text-sm"
              data-testid="case-studies-admin-link"
            >
              Admin Panel
            </button>
          )}
        </div>
      </div>

      {/* Hero Section */}
      <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 py-20">
        <div className="container mx-auto px-6 text-center">
          <p className="text-sm text-blue-400 uppercase tracking-wider mb-4 font-semibold">{t('nav.caseStudies')}</p>
          <h1 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            {t('pages.caseStudies.title')}
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            {t('pages.caseStudies.subtitle')}
          </p>
        </div>
      </div>

      {/* Case Studies Grid */}
      <div className="container mx-auto px-6 py-16">
        {isLoading ? (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="w-12 h-12 animate-spin text-blue-600" />
          </div>
        ) : caseStudies.length === 0 ? (
          <div className="text-center py-20">
            <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {i18n.language === 'tr' ? 'Henüz vaka çalışması yok' : 'No case studies yet'}
            </h3>
            <p className="text-gray-500">
              {i18n.language === 'tr' ? 'Yakında yeni vaka çalışmaları eklenecek' : 'New case studies coming soon'}
            </p>
          </div>
        ) : (
          <div className="space-y-12">
            {caseStudies.map((study, index) => (
              <div 
                key={study.id}
                className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300"
              >
                <div className={`grid lg:grid-cols-2 ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                  {/* Image */}
                  <div className={`relative h-64 lg:h-auto ${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                    <img 
                      src={study.heroImage}
                      alt={study.company}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent lg:bg-gradient-to-r" />
                    <div className="absolute bottom-6 left-6">
                      <div className="flex items-center gap-3">
                        <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center font-bold text-xl text-gray-900">
                          {study.logo || study.company?.substring(0, 2).toUpperCase()}
                        </div>
                        <div>
                          <h3 className="text-white font-bold text-xl">{study.company}</h3>
                          <p className="text-gray-300 text-sm">{study.industry}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className={`p-8 ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                    {/* Results Preview */}
                    <div className="grid grid-cols-2 gap-4 mb-6">
                      {(study.results || []).slice(0, 2).map((result, idx) => (
                        <div key={result.metric || `result-${idx}`} className="bg-blue-50 rounded-lg p-4">
                          <div className="text-2xl font-bold text-blue-600">{result.metric}</div>
                          <div className="text-sm text-gray-600">{result.label}</div>
                        </div>
                      ))}
                    </div>

                    {/* Challenge Preview */}
                    <h4 className="text-sm font-semibold text-gray-500 uppercase mb-2">{t('pages.caseStudies.challenge')}</h4>
                    <p className="text-gray-600 mb-6 line-clamp-3">{study.challenge}</p>

                    {/* Meta Info */}
                    <div className="flex flex-wrap gap-4 mb-6 text-sm text-gray-500">
                      <div className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {study.timeline}
                      </div>
                      <div className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        {study.participants}
                      </div>
                    </div>

                    {/* CTA */}
                    <Button 
                      onClick={() => onCaseStudyClick(study.id)}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      {t('pages.caseStudies.viewStudy')}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* CTA Section */}
      <div className="bg-gray-900 py-16">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            {i18n.language === 'tr' ? 'Başarı Hikayenizi Yazmaya Hazır mısınız?' : 'Ready to Write Your Success Story?'}
          </h2>
          <p className="text-gray-400 mb-8 max-w-2xl mx-auto">
            {i18n.language === 'tr' ? 'Organizasyonunuzun liderlik gelişimini dönüştürmek için bu sektör liderlerine katılın' : 'Join these industry leaders in transforming your organization\'s leadership development'}
          </p>
          <Button 
            onClick={openBookDemo}
            className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg"
          >
            {i18n.language === 'tr' ? 'Demo Talep Et' : 'Book a Demo'}
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CaseStudies;
